import { Capacitor } from '@capacitor/core';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { compare, parse } from 'semver';

import { usePortalPublicConfiguration } from '@frontend/portal-public-configuration';

import { useMetadata } from '../metadata-context';
import { VolatileKeyValueStore } from '../storage';
import {
  VersionExpectationStateType,
  VersionExpectationsContext,
} from './version-expectations.context';

const STORAGE_KEY = 'version_expectations_ignore_preferred';

export const VersionExpectationsProvider = (props: PropsWithChildren) => {
  const { appVersion } = useMetadata();
  const config = usePortalPublicConfiguration();
  const [state, setState] = useState<VersionExpectationStateType>('LOADING');

  const preferredVersion = config.versionExpectations?.preferred;
  const requiredVersion = config.versionExpectations?.required;

  const updateVersionExpectationState = useCallback(() => {
    if (!Capacitor.isNativePlatform()) {
      setState('SATISFIED');
      return;
    }

    VolatileKeyValueStore.get(STORAGE_KEY).then((ignoredVersion) => {
      let semverVersion =
        appVersion === 'local'
          ? '99.99'
          : appVersion.startsWith('dev')
            ? '99.99'
            : appVersion.startsWith('latest')
              ? (parse(appVersion)?.version ?? '99.99')
              : appVersion;

      semverVersion =
        semverVersion.split('.').length === 3
          ? semverVersion
          : // Dev versions are not full semver versions so we
            // adjust for that so we are fully backwards
            // compatible with old APKs updated via capgo.
            `${semverVersion}.0`;

      const preferredVersionOutdated = preferredVersion
        ? compare(preferredVersion, semverVersion) === 1
        : false;
      const requiredVersionOutdated = requiredVersion
        ? compare(requiredVersion, semverVersion) === 1
        : false;

      if (requiredVersionOutdated) {
        setState('REQUIRED_OUTDATED');
        return;
      }

      if (preferredVersionOutdated && preferredVersion !== ignoredVersion) {
        setState('MINIMUM_OUTDATED');
        return;
      }

      setState('SATISFIED');
    });
  }, [appVersion, requiredVersion, preferredVersion]);

  const ignoreUpdate = useCallback(() => {
    if (!preferredVersion) {
      return;
    }

    VolatileKeyValueStore.set(STORAGE_KEY, preferredVersion).then(
      updateVersionExpectationState,
    );
  }, [appVersion, requiredVersion, preferredVersion]);

  useEffect(updateVersionExpectationState, [
    state === 'LOADING',
    appVersion,
    requiredVersion,
    preferredVersion,
  ]);

  const value = useMemo(() => {
    return {
      state,
      onIgnoreUpdate: ignoreUpdate,
    };
  }, [state, ignoreUpdate]);

  return (
    <VersionExpectationsContext.Provider value={value}>
      {props.children}
    </VersionExpectationsContext.Provider>
  );
};
